import { AuthoringGoal } from './authoring/AuthoringGoal';
import { FeatureLesson } from './learning/FeatureLesson';
/**
 * CreateInterviewForm, workspace-aware form-first wizard.
 *
 * The wizard's job: walk an admin from "I have a workspace" to "I have a
 * structured form ready to share." It hands the result back to its parent
 * (`AdminSetupPage` in `App.tsx`) via `onSetupComplete`.
 *
 * Phase A of the Workspaces feature removes the URL step entirely. URL,
 * brand voice/tone, target audience, value proposition and keywords are read
 * from `useOrg().activeWorkspace` (sourced from the `brands` table under the
 * Phase A naming contract). The wizard starts directly on the objectives
 * step. The chat composer hosts a `WorkspaceDropdown` in its left-actions
 * slot so the user can switch the brand context without leaving the modal.
 *
 * Step ordering:
 *   1. Objectives, single-row dense list rendered inside an animated-border
 *      "AI surface" container. A chat-style composer at the top accepts a
 *      free-form objective (with mic + file attach); rows below are
 *      brand-tailored AI suggestions from `POST /objectives/suggest`. The
 *      worker call composes its `productResearch` payload from the active
 *      workspace's columns (summary, target_audience, brand_voice +
 *      brand_tone joined with ", ", value_proposition, keywords). While the
 *      worker call runs the suggestion slot stays empty; once the cards
 *      arrive they reveal one-by-one on a 220ms cadence so the user watches
 *      the list build itself rather than seeing a populated panel pop in.
 *   2. Builder canvas, `<FormBuilder>` mounted directly. The existing SSE
 *      `/forms/generate` call fires on mount (also threading the workspace
 *      `productResearch` payload); streamed `FormField` cards reveal one at
 *      a time. A "Settings" button opens a slide-over with all the legacy
 *      advanced toggles (relocated, behaviour-preserved).
 *   3. Save, calls `onSetupComplete` with the form_schema and the active
 *      workspace's id as `brandId`. Does NOT pass `details`
 *      `interviewService.createInterviewSession` auto-derives.
 *
 * Streaming AI reveal: each `field` event from the worker pushes a card into
 * `fields`. The FormBuilder reveals each card on a steady cadence so the
 * canvas reads as a build rather than a flood.
 *
 * Failure handling, NO silent fallbacks. If `/objectives/suggest` or
 * `/forms/generate` fails (worker error, network, empty response, etc.) the
 * wizard surfaces an error block with the underlying message and a Retry
 * button. PostHog still captures every failure for telemetry. We deliberately
 * never substitute static template strings for AI output, that gaslights the
 * admin into thinking the AI produced generic phrasings. Failures must be
 * visible so the underlying problem (env var, deploy, key, etc.) gets
 * diagnosed instead of papered over.
 *
 * Empty state: when no workspace is active (e.g. the user is between orgs
 * or the workspaces fetch is still loading), the wizard renders a guidance
 * card pointing at the workspace switcher rather than the AI surfaces. This
 * keeps the wizard from crashing when `activeWorkspace` is null and gives
 * the user a clear next action.
 *
 * Edit mode (`editSession` prop, see `AdminEditPage`): the wizard becomes an
 * in-place editor for that campaign instead of a create flow. It skips the
 * objectives step entirely and lands directly on the builder canvas (step 2),
 * with fields normalized from `editSession.form_schema` (existing ids and valid
 * names preserved) and title/goal/language/every
 * settings toggle seeded from the session row. `/forms/generate` never fires
 * and no setup-draft is read or saved, edit mode always re-seeds from the
 * source schema instead. Info-block image storage keys already referenced by
 * the saved schema are passed to `<FormBuilder>` as `committedImageKeys`, so
 * removing a card or replacing its image never fires the best-effort R2
 * delete against media a saved campaign (or in-flight session) still points
 * at. The save button reads "Save"; the host performs the actual
 * update on `onSetupComplete`. Clone mode commits the source campaign's image
 * keys the same way, because the clone shares them with the live source.
 *
 * File storage release gate: until `fileStorage` is released, generated File
 * upload questions are withheld from the canvas and the Drive folder picker
 * is hidden. Saved, cloned and author-converted file questions stay.
 *
 * @module components/CreateInterviewForm
 */

import React, { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ButtonContent } from './ui/ButtonContent';
import { DropdownMenu } from './ui/DropdownMenu';
import { IconDots } from '@tabler/icons-react';
import { InterviewContext, FormField, InterviewSession } from '../types';
import { loadSetupDraft, saveSetupDraft, type SetupDraft } from './setupDraft';
import type { SessionSettingsUpdate } from '../services/interviewService';
import { validateInformationBlocks, validateCalendarBookings } from '../services/interviewService';
import { useOrg } from '../lib/OrgProvider';
import { useSupabase } from '../lib/SupabaseProvider';
import { StorageDestinationPicker } from './form-builder/StorageDestinationPicker';
import { AnimatedLabel } from './ui/AnimatedLabel';
import { AnimatedPresence } from './ui/AnimatedPresence';
import { ConversationSettingsPanel } from './conversation-settings/ConversationSettingsPanel';
import { buildConversationSettingsValues, prepareConversationSettingsSave, validateConversationSettings, type ConversationSettingsValues } from './conversation-settings/settings-model';
import { useConversationSettingsAccess } from './conversation-settings/useConversationSettingsAccess';
import { DEFAULT_OUTPUT_LANGUAGE } from '../lib/languages';
import { labelToName, type FormTemplateKey } from '../lib/form-templates';
import type { SuggestedObjective } from '../lib/suggest-objectives';
import { collectQuestionImageStorageKeys, FormBuilder, useInformationBlockAbandonCleanup } from './form-builder';
import { QuestionEditorSurface } from './form-builder/QuestionEditorSurface';
import { QuestionPreview } from './form-builder/QuestionPreview';
import { Skeleton } from './ui/Skeleton';
import { ObjectiveChatInput } from './ObjectiveChatInput';
import type { InlineAttachment } from '../lib/file-attachment';
import { resolveGenerationErrorMessage } from '../lib/generation-errors';
import type { Workspace } from '../lib/org-types';
import { WorkspaceDropdown } from './workspace/WorkspaceDropdown';
import { useBrandResearchLabel } from '../lib/brand-research';
import { useSuggestionReveal } from './hooks/useSuggestionReveal';
import { useBrandResearchPoll } from './hooks/useBrandResearchPoll';
import { useFormGenerationStream } from './hooks/useFormGenerationStream';
import { FormImportNotes } from './form-builder/FormImportNotes';
import { captureEvent, captureException } from '../lib/telemetry/posthog/frontend';
import { buildConversationCreationProps } from '../lib/telemetry/conversation-creation-contract';
import type { ConversationExperienceConfig } from '../lib/multimodal-contract';
import { normalizeConversationDefinition } from '../lib/conversation-definition';
import { validateConversationSections, type ConversationSection } from '../lib/conversation-sections';
import { reconcileBuilderSections } from './form-builder/sections/section-editor';
import { FORM_FIELD_LIMITS, validateFormFields } from '../lib/form-capabilities';
import { useReleaseFeature } from '../lib/release-flags';

/**
 * Synthetic always-visible "start from scratch" option. Rendered as its own
 * row beneath the AI suggestion list (and the error / loading blocks) so the
 * user can always opt out of generation when they want to author questions
 * by hand. Uses `templateKey: 'custom'` so the type contract still lines up
 * with `SuggestedObjective`; the wizard short-circuits the generation call
 * via `handleStartFromScratch` when this row is picked.
 */
const START_FROM_SCRATCH_OBJECTIVE: SuggestedObjective = {
  title: 'Start blank',
  hint: 'Add your own questions',
  templateKey: 'custom',
};

/**
 * Withholds generated File upload questions while file storage is unreleased.
 * Returns the same array when nothing is withheld so downstream memos stay
 * stable.
 *
 * @param fields Every question the canvas currently holds.
 * @param authoredFileFieldIds File questions that stay: saved, cloned, or
 *   converted in place by the author.
 */
function withholdGeneratedFileQuestions(
  fields: FormField[],
  authoredFileFieldIds: ReadonlySet<string>,
): FormField[] {
  const visible = fields.filter((field) => field.type !== 'file_upload' || authoredFileFieldIds.has(field.id));
  return visible.length === fields.length ? fields : visible;
}

/**
 * Compose the `productResearch` payload threaded into both worker calls
 * (`/objectives/suggest` and `/forms/generate`) from a workspace row. Returns
 * `undefined` when no workspace is active, which short-circuits the request
 * body so the worker can fall back to its own heuristics.
 *
 * Voice + tone are joined with ", " into a single `brandVoiceTone` string so
 * the legacy worker contract stays unchanged. Brand-research v2 adds five
 * rich slots (canonical name, offerings, voice attributes, tone attributes,
 * style guide). Each is passed through as `undefined` when the workspace
 * does not have it, so the worker's render helper skips the section instead
 * of emitting an empty heading. The raw URL, colors, fonts, and the
 * (non-canonical) workspace name are intentionally NOT forwarded.
 */
function workspaceToResearchPayload(
  workspace: Workspace | null,
):
  | {
      brandNameCanonical?: string;
      privacyPolicyUrl?: string;
      summary: string;
      keyFeatures: string[];
      targetAudience: string;
      valueProposition: string;
      brandVoiceTone: string;
      offerings?: NonNullable<Workspace['offerings']>;
      voiceAttributes?: NonNullable<Workspace['voice_attributes']>;
      toneAttributes?: NonNullable<Workspace['tone_attributes']>;
      styleGuide?: NonNullable<Workspace['style_guide']>;
    }
  | undefined {
  if (!workspace) return undefined;
  const voiceTone = [workspace.brand_voice, workspace.brand_tone]
    .filter((part): part is string => !!part && part.length > 0)
    .join(', ');
  return {
    brandNameCanonical: workspace.brand_name_canonical ?? undefined,
    privacyPolicyUrl: workspace.privacy_policy_url ?? undefined,
    summary: workspace.summary ?? '',
    keyFeatures: workspace.keywords ?? [],
    targetAudience: workspace.target_audience ?? '',
    valueProposition: workspace.value_proposition ?? '',
    brandVoiceTone: voiceTone,
    offerings: workspace.offerings ?? undefined,
    voiceAttributes: workspace.voice_attributes ?? undefined,
    toneAttributes: workspace.tone_attributes ?? undefined,
    styleGuide: workspace.style_guide ?? undefined,
  };
}

/**
 * Options passed alongside `InterviewContext` on setup completion.
 * Phase 1 adds `formSchema`, the parent threads this through to
 * `createInterviewSession`. The service auto-derives `details` from it.
 */
export interface SetupOptions {
  /** Versioned respondent settings persisted alongside the authored definition. */
  conversationConfig?: ConversationExperienceConfig | null;
  videoEnabled?: boolean;
  campaignSettings?: SessionSettingsUpdate;
  /** Admin-designed form fields. Replaces direct `details` authoring. */
  formSchema?: FormField[];
}

/**
 * Possible wizard step states.
 *
 * - `'objectives'` , dense single-row list (free-form input + AI rows)
 *                     inside an animated-border container. The default
 *                     initial step in the workspace-aware build.
 * - `'builder'`    , FormBuilder canvas (handles SSE reveal + final Save).
 *
 * `'url'` (legacy URL-collection step), `'context'`, `'review'`, `'objective'`
 * and `'template'` from earlier iterations are all gone. Advanced settings
 * live in a slide-over on `'builder'`.
 */
export type SetupStep = 'objectives' | 'builder';

interface CreateInterviewFormProps {
  initialConversationConfig?: ConversationExperienceConfig | null;
  /** Called when the admin clicks Create on the builder step. */
  onSetupComplete: (context: InterviewContext, options?: SetupOptions) => void;
  /**
   * Optional pre-filled context (used when re-entering the wizard with a
   * pre-chosen objective, e.g. via deep link). The URL/brand context is
   * always sourced from `activeWorkspace`; only `goal` and `outputLanguage`
   * are honoured here.
   */
  initialContext?: InterviewContext;
  /**
   * Optional pre-loaded fields. When provided non-empty (e.g. from the
   * dashboard's "Clone Interview" action) the wizard skips the objectives
   * step, lands directly on the builder with these fields, and does NOT
   * fire `/forms/generate`, the cloned schema is the starting point. Their
   * uploaded images still belong to the source campaign, so they are
   * committed: abandon cleanup, card removal and image replacement never
   * delete them.
   */
  initialFields?: FormField[];
  /** Optional initial video setting from a previous session. */
  initialVideoEnabled?: boolean;
  /** Notify the parent on every step change (for header step indicator). */
  onStepChange?: (step: SetupStep) => void;
  /**
   * The campaign being edited IN PLACE. When present the wizard becomes an
   * editor: it lands directly on the builder (no objectives step, no
   * back-to-objectives button, no /forms/generate call, no setup-draft
   * restore or save), normalizes fields from `form_schema` (ids and valid
   * names preserved), seeds title/goal/language and every settings toggle
   * from the session row (re-gated through `can()`), and labels the save
   * button "Save". Info-block images already referenced by the
   * saved schema are committed: they are never deleted by card removal,
   * image replacement, or abandon cleanup. The host performs the actual
   * update on `onSetupComplete`.
   */
  editSession?: InterviewSession;
  /** Existing responses constrain the shared response-limit setting. */
  responseCount?: number;
  /** Header destination for editor actions; undefined keeps the creation footer. */
  actionsTarget?: HTMLElement | null;
  /** Host persistence state, displayed in the stable save button. */
  savePending?: boolean;
  /** Temporary success feedback, displayed inside the save action. */
  saveSucceeded?: boolean;
}

const CreateInterviewForm: React.FC<CreateInterviewFormProps> = ({
  onSetupComplete,
  initialContext,
  initialFields,
  initialVideoEnabled,
  onStepChange,
  editSession,
  responseCount = 0,
  actionsTarget,
  savePending = false,
  saveSucceeded = false,
  initialConversationConfig,
}) => {
  // Clone path: the dashboard explicitly hands us a fields array (even an
  // empty one for legacy sessions that predate `form_schema`). Treat the
  // mere presence of the prop as the clone signal so we always skip the
  // objectives step and land on the builder. Captured at mount only
  // subsequent prop changes don't reset the wizard.
  const isCloneMode = initialFields !== undefined;
  // Edit path: the wizard edits an existing campaign in place. Behaves like
  // clone for step/draft handling, but normalizes the definition and preserves
  // settings from the session row.
  const isEditMode = editSession !== undefined;
  const [compactActions, setCompactActions] = useState(false);
  useLayoutEffect(() => {
    const row = actionsTarget?.closest<HTMLElement>('[data-campaign-command-row]');
    if (!row) { setCompactActions(false); return undefined; }
    const measure = () => {
      const width = row.getBoundingClientRect().width;
      setCompactActions(width > 0 && width < 720);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [actionsTarget]);
  const fileStorageEnabled = useReleaseFeature('fileStorage');
  // The offline-session worker requires the admin's Supabase JWT on every
  // call (audit S3); the session is read at generation time below.
  const { session, supabase } = useSupabase();
  const {
    activeOrg,
    activeWorkspace,
    workspaces,
    setActiveWorkspaceId,
    refreshActiveWorkspace,
  } = useOrg();
  const { can, languagePolicy: interviewLanguagePolicy } = useConversationSettingsAccess();
  const settingsDefaults = useMemo(() => buildConversationSettingsValues(editSession), [editSession]);

  // ── Derived workspace context ─────────────────────────────────────────
  // URL/name/research come from the active workspace, not local state.
  const targetUrl = activeWorkspace?.website_url ?? '';
  const targetName =
    activeWorkspace?.name ?? activeOrg?.name ?? 'Untitled';
  const researchWorkspace = isEditMode
    ? workspaces.find(workspace => workspace.id === editSession?.brand_id)
      ?? (activeWorkspace?.id === editSession?.brand_id ? activeWorkspace : null)
    : activeWorkspace;
  const productResearch = useMemo(
    () => workspaceToResearchPayload(researchWorkspace),
    [researchWorkspace],
  );

  // ── In-session draft restore ──────────────────────────────────────────
  // Resume an unfinished build (e.g. after the user popped into Settings or
  // reloaded the tab). Skipped for clones, which always re-seed from their
  // source schema. Loaded once at mount; the scalar `useState` initializers
  // below read from it, and `fields` is rehydrated in the mount effect. The
  // draft is cleared on create success (AdminSetupPage) and on an explicit
  // "Create" (the sidebar/dashboard CTAs), so a normal mount here
  // only ever restores a genuinely in-progress build.
  const [restoredDraft] = useState<SetupDraft | null>(() =>
    isCloneMode || isEditMode ? null : loadSetupDraft(),
  );
  const [importReportState, setImportReportState] = useState<SetupDraft['importReport']>(() =>
    restoredDraft?.importReport?.workspaceId === activeWorkspace?.id ? restoredDraft?.importReport : undefined,
  );
  const importReport = importReportState?.workspaceId === activeWorkspace?.id ? importReportState : undefined;

  // ── Step state ─────────────────────────────────────────────────────────
  const [step, setStep] = useState<SetupStep>(
    restoredDraft?.step ?? (isCloneMode || isEditMode ? 'builder' : 'objectives'),
  );
  const [initialDefinition] = useState(() => normalizeConversationDefinition({
    fields: editSession ? editSession.form_schema : isCloneMode ? initialFields : restoredDraft?.fields,
    config: (editSession ? settingsDefaults.conversation_config : undefined) ?? initialConversationConfig ?? restoredDraft?.conversationConfig
      ?? (!isEditMode && !isCloneMode && !restoredDraft && !initialVideoEnabled ? { version: 1, callsEnabled: false } : undefined),
    details: editSession?.details ?? (isCloneMode ? initialContext?.details : undefined),
    goal: editSession?.goal ?? (isCloneMode ? initialContext?.goal : undefined),
    requiredVideo: editSession?.require_video ?? restoredDraft?.requireVideo,
  }));
  const [conversationConfig, setConversationConfig] = useState<ConversationExperienceConfig>(initialDefinition.conversationConfig);
  const [checkingDestination, setCheckingDestination] = useState(false);
  const [destinationError, setDestinationError] = useState<string | null>(null);
  const destinationSaveRef = useRef(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // ── Title + goal state ────────────────────────────────────────────────
  // The form's display title (`formTitle`) and its goal (`objective`) are
  // independent fields in this wizard. The inline-editable textarea at the
  // top of the builder drives `formTitle` (saved as `target_name`), that's
  // the human-readable name the admin sees in the dashboard, sidebar, and
  // breadcrumbs. The goal lives in the Advanced Settings slide-over and is
  // what holito uses to steer the conversation. Title is seeded eagerly
  // from the picked objective (already concise); goal stays empty until
  // the SSE `goal` event lands with a formulated 1-2 sentence directive,
  // so the raw bubble label / typed prompt never gets saved verbatim.
  const [formTitle, setFormTitle] = useState(
    restoredDraft?.formTitle ?? editSession?.target_name ?? initialContext?.targetName ?? '',
  );
  const [objective, setObjective] = useState(
    restoredDraft?.objective ?? editSession?.goal ?? initialContext?.goal ?? '',
  );

  /**
   * Free-text objective typed into the chat-style composer at the top of
   * the step. Replaces the legacy "Something else?" disclosure. The composer
   * itself owns mic + file affordances; this slot just holds the typed text.
   */
  const [freeFormObjective, setFreeFormObjective] = useState(
    restoredDraft?.freeFormObjective ?? '',
  );

  /**
   * Auto-grow ref for the inline form-title textarea. Mirrors the per-card
   * label autosize so a long objective wraps onto a second line instead of
   * being clipped by a single-row textarea.
   */
  const titleRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [formTitle, step]);

  // ── Advanced settings state (preserved, relocated to slide-over) ─────
  const [outputLanguage, setOutputLanguage] = useState(
    restoredDraft?.outputLanguage ??
      (editSession ? settingsDefaults.output_language : undefined) ??
      initialContext?.outputLanguage ??
      DEFAULT_OUTPUT_LANGUAGE,
  );
  // Shared settings validate access and normalize hidden media controls at save.
  const [videoEnabled, setVideoEnabled] = useState(
    (restoredDraft?.videoEnabled ??
      (editSession ? settingsDefaults.video_enabled : undefined) ??
      initialVideoEnabled ??
      false),
  );
  const [screenShareEnabled, setScreenShareEnabled] = useState(
    restoredDraft?.screenShareEnabled ?? settingsDefaults.screen_share_enabled,
  );
  const [maxResponses, setMaxResponses] = useState<number | null>(
    restoredDraft?.maxResponses ?? settingsDefaults.max_responses,
  );

  const [allowDuplicateEmails, setAllowDuplicateEmails] = useState(
    restoredDraft?.allowDuplicateEmails ?? settingsDefaults.allow_duplicate_emails,
  );
  const requireName = false;
  const requireEmail = false;
  const [requireVideo, setRequireVideo] = useState(
    restoredDraft?.requireVideo ?? settingsDefaults.require_video,
  );
  const [enforceLanguage, setEnforceLanguage] = useState<string | null>(
    restoredDraft?.enforceLanguage ??
      settingsDefaults.enforce_language ??
      interviewLanguagePolicy.enforcedLanguage,
  );
  // Both settings entry points use the same defaults and controlled panel.
  const identityCollection = 'never' as const;
  // Plan-gated like videoEnabled: a restored/seeded 'redirect' downgrades to
  // 'message' when the plan no longer includes completion_redirect.
  const [completionAction, setCompletionAction] = useState<'message' | 'redirect'>(() => {
    const seeded =
      restoredDraft?.completionAction ??
      settingsDefaults.completion_action;
    return seeded === 'redirect' && can('completion_redirect') ? 'redirect' : 'message';
  });
  const [completionMessage, setCompletionMessage] = useState<string | null>(
    restoredDraft?.completionMessage ?? settingsDefaults.completion_message,
  );
  const [completionRedirectUrl, setCompletionRedirectUrl] = useState<string | null>(
    restoredDraft?.completionRedirectUrl ?? settingsDefaults.completion_redirect_url,
  );
  const [opensAt, setOpensAt] = useState<string | null>(
    restoredDraft?.opensAt ?? settingsDefaults.opens_at,
  );
  const [closesAt, setClosesAt] = useState<string | null>(
    restoredDraft?.closesAt ?? settingsDefaults.closes_at,
  );

  // Slide-over open state.
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Keep enforced language in lockstep with plan policy.
  useEffect(() => {
    if (!interviewLanguagePolicy.allowLanguageChoice) {
      setEnforceLanguage(interviewLanguagePolicy.enforcedLanguage);
    }
  }, [
    interviewLanguagePolicy.allowLanguageChoice,
    interviewLanguagePolicy.enforcedLanguage,
  ]);

  // Scroll to top on step change.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let scrollParent: HTMLElement | null = el.parentElement;
    while (scrollParent) {
      const overflow = getComputedStyle(scrollParent).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') break;
      scrollParent = scrollParent.parentElement;
    }
    (scrollParent ?? window).scrollTo({
      top: 0,
      behavior: 'instant' as ScrollBehavior,
    });
  }, [step]);

  // Notify parent of step changes.
  useEffect(() => {
    onStepChange?.(step);
    captureEvent('conversation_creation.step_reached', buildConversationCreationProps(activeWorkspace?.id, { step }));
  }, [step, onStepChange, activeWorkspace?.id]);

  // ── Suggestion reveal (workspace-synced staggered card reveal) ────────
  const {
    suggestedObjectives,
    showAllObjectives,
    revealedInitialCount,
    revealedExtraCount,
    handleShowMore,
  } = useSuggestionReveal({
    activeWorkspaceId: activeWorkspace?.id,
    suggestedObjectivesGeneratedAt: activeWorkspace?.suggested_objectives_generated_at,
    suggestedObjectivesCache: (activeWorkspace?.suggested_objectives as SuggestedObjective[] | null) ?? null,
  });

  // ── Brand-research polling for late-arriving suggestions ──────────────
  const { showResearchPlaceholder } = useBrandResearchPoll({
    activeWorkspace,
    refreshActiveWorkspace,
  });

  // Shared SSE tracker so the placeholder shows the same dynamic copy as
  // WorkspaceSettings (e.g. "Exploring holi.to/pricing").
  const researchLabel = useBrandResearchLabel(activeWorkspace?.id);

  // ── SSE form-generation stream ─────────────────────────────────────────
  const {
    fields: canvasFields,
    setFields,
    isGenerating,
    setIsGenerating,
    awaitingTitle,
    setAwaitingTitle,
    generationError,
    setGenerationError,
    runGeneration,
    handleRetryGeneration,
    abortGeneration,
    lastGenAttemptRef,
  } = useFormGenerationStream({
    multimodalVersion: conversationConfig.version,
    productResearch,
    accessToken: session?.access_token ?? '',
    workspaceId: activeWorkspace?.id ?? null,
    organizationId: activeWorkspace?.organization_id ?? null,
    onTitle: (t) => setFormTitle((prev) => (prev.trim() ? prev : t)),
    onGoal: (g) => setObjective((prev) => (prev.trim() ? prev : g)),
    onSections: (sections) => setConversationConfig(previous => sections.length || previous.sections !== undefined ? { ...previous, sections } : previous),
    onImportReport: (report) => setImportReportState(report && activeWorkspace ? { workspaceId: activeWorkspace.id, report } : undefined),
    // Normalize before the first paint; persisted response snapshots remain
    // untouched, and definition changes are saved only through the usual action.
    initialFields: initialDefinition.fields,
  });

  // ── File storage release gate ─────────────────────────────────────────
  // Until Drive storage is released, generated File upload questions (whole
  // conversation, single question, or a restored unsaved draft) are withheld
  // so the canvas never holds a question that can never be saved. Saved
  // (edit) and cloned file questions stay, as does a question the author
  // converts in place. Withheld questions reappear once the release resolves.
  const [authoredFileFieldIds, setAuthoredFileFieldIds] = useState<ReadonlySet<string>>(() => new Set(
    isEditMode || isCloneMode
      ? initialDefinition.fields.filter((field) => field.type === 'file_upload').map((field) => field.id)
      : [],
  ));
  const fields = useMemo(
    () => fileStorageEnabled ? canvasFields : withholdGeneratedFileQuestions(canvasFields, authoredFileFieldIds),
    [authoredFileFieldIds, canvasFields, fileStorageEnabled],
  );
  // Keep withheld file membership in the draft; authoring and save use only
  // the visible questions, including the original position of empty sections.
  const visibleSections = useMemo(
    () => conversationConfig.sections && fields !== canvasFields
      ? reconcileBuilderSections(canvasFields, fields, conversationConfig.sections)
      : conversationConfig.sections,
    [canvasFields, conversationConfig.sections, fields],
  );
  // Live view of the visible canvas for callbacks (abandon cleanup, edits).
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  // Persist the in-session draft on every change so an in-app navigation (the
  // settings cog) or a same-tab reload can resume this build. Skipped for
  // clones and edits, which always re-seed from their source schema. The
  // draft is cleared on create success and on an explicit "Create".
  useEffect(() => {
    if (isCloneMode || isEditMode) return;
    saveSetupDraft({
      ...(importReport ? { importReport } : {}),
      conversationConfig,
      step,
      formTitle,
      objective,
      freeFormObjective,
      outputLanguage,
      videoEnabled,
      screenShareEnabled,
      maxResponses,
      allowDuplicateEmails,
      requireName,
      requireEmail,
      requireVideo,
      enforceLanguage,
      identityCollection,
      completionAction,
      completionMessage,
      completionRedirectUrl,
      opensAt,
      closesAt,
      fields: canvasFields,
    });
  }, [
    importReport,
    conversationConfig,
    isCloneMode,
    isEditMode,
    step,
    formTitle,
    objective,
    freeFormObjective,
    outputLanguage,
    videoEnabled,
    screenShareEnabled,
    maxResponses,
    allowDuplicateEmails,
    requireName,
    requireEmail,
    requireVideo,
    enforceLanguage,
    identityCollection,
    completionAction,
    completionMessage,
    completionRedirectUrl,
    opensAt,
    closesAt,
    canvasFields,
  ]);

  /** The generation failure's single action, focused when it appears. */
  const generationActionRef = useRef<HTMLButtonElement | null>(null);

  // Keyboard recovery: the clicked objective card unmounts when the builder
  // opens, so focus is usually lost by the time a failure lands. Move it to
  // the failure's action, but never steal it from a field the creator is
  // using (for example the title). The alert region announces the copy, so
  // the focus move adds no duplicate announcement.
  useEffect(() => {
    if (!generationError) return;
    const active = document.activeElement;
    if (!active || active === document.body) generationActionRef.current?.focus();
  }, [generationError]);

  /**
   * Cancel any in-flight SSE generation and wipe the canvas when leaving
   * the builder for the objectives step. Guarantees the next objective
   * pick starts from an empty FormBuilder rather than inheriting stale
   * fields or a still-streaming generation.
   */
  const handleBackToObjectives = useCallback(() => {
    abortGeneration();
    setImportReportState(undefined);
    setFields([]);
    setConversationConfig(previous => previous.sections === undefined ? previous : { ...previous, sections: [] });
    setGenerationError(null);
    setIsGenerating(false);
    setAwaitingTitle(false);
    captureEvent('conversation_creation.abandoned', buildConversationCreationProps(activeWorkspace?.id, { step }));
    setStep('objectives');
  }, [abortGeneration, setFields, setGenerationError, setIsGenerating, setAwaitingTitle, activeWorkspace?.id, step]);

  /**
   * Commit the chosen `{ objective, template }` and advance to the builder.
   * Used by both the row click handler and the chat-input submit. The
   * attachment is forwarded to `runGeneration` so Gemini sees the file as
   * an `inlineData` part alongside the prompt.
   *
   * `seedTitle` is the title to display eagerly while generation streams in.
   * The card path passes the card's already-concise title so the headline
   * appears instantly. The free-form chat path passes an empty string and
   * lets `runGeneration`'s `title` SSE event populate the headline, that's
   * how a long prompt informs the goal without becoming the form title.
   *
   * Note: `selectedObjective` (the raw bubble label or chat text) drives
   * Gemini's question generation but is NOT copied into the form's saved
   * `goal`. The goal slot stays empty until the SSE `goal` event lands
   * with a formulated 1-2 sentence directive, that's the fix for the
   * bug where a long stream-of-consciousness prompt was being saved
   * verbatim as the call's goal.
   */
  const commitObjectiveAndAdvance = useCallback(
    (
      selectedObjective: string,
      selectedTemplate: FormTemplateKey,
      attachment: InlineAttachment | null = null,
      seedTitle: string = '',
      objectiveHint: string | undefined = undefined,
      templateId: string | undefined = undefined,
    ) => {
      setFormTitle(seedTitle);
      // Only the freeform path (empty `seedTitle`) is awaiting an AI title;
      // card paths come in pre-titled and never need the skeleton.
      setAwaitingTitle(!seedTitle);
      // Goal stays empty here, the SSE `goal` event populates it. We
      // deliberately do NOT seed `objective` from `selectedObjective`,
      // because that would copy the bubble label / typed prompt straight
      // into the form's saved goal (the original bug).
      setObjective('');
      setStep('builder');
      void runGeneration(selectedObjective, selectedTemplate, attachment, objectiveHint, templateId);
    },
    [runGeneration],
  );

  const handleSelectObjective = useCallback(
    (card: SuggestedObjective) => {
      // AI-suggestion rows don't carry an attachment, they're synthesised
      // from brand context only, so generation runs without a file. The
      // card's title is already concise (sourced from `/objectives/suggest`)
      // so seed the form-title slot eagerly; the SSE `title` event won't
      // overwrite a non-empty title. The form's GOAL stays empty until the
      // SSE `goal` event lands so the bubble label is never saved as the
      // call's goal verbatim.
      //
      // The card's `hint` carries the form's WHY (e.g. "Capture beta
      // interest from founders for SoPhy.") and is forwarded to the worker
      // so the goal-writer prompt sees the original intent, not just the
      // short bubble label.
      captureEvent('conversation_creation.objective_selected', buildConversationCreationProps(activeWorkspace?.id, { type: 'ai_suggestion', objective: card.title }));
      commitObjectiveAndAdvance(
        card.title,
        card.templateKey,
        null,
        card.title,
        card.hint,
        card.templateId,
      );
    },
    [commitObjectiveAndAdvance],
  );

  /**
   * Open the builder canvas blank, no AI generation. Used by the always-on
   * `START_FROM_SCRATCH_OBJECTIVE` row so users who already know exactly
   * what they want to ask aren't forced through the AI suggestion path.
   *
   * Cancels any in-flight generation, clears canvas state, and sets a
   * sensible default `objective` so the eventual save call still has a goal
   * string. We deliberately do NOT call `runGeneration`, picking this row
   * means "I want to design questions myself."
   */
  const handleStartFromScratch = useCallback(() => {
    abortGeneration();
    setImportReportState(undefined);
    lastGenAttemptRef.current = null;
    setFields([]);
    setConversationConfig(previous => previous.sections === undefined ? previous : { ...previous, sections: [] });
    setGenerationError(null);
    setIsGenerating(false);
    setAwaitingTitle(false);
    // Title and goal both start blank for the from-scratch path, the admin
    // is designing the form by hand, so we don't presume to seed either with
    // the synthetic "Start blank" placeholder. Save falls back to the
    // workspace name when `formTitle` stays empty.
    setFormTitle('');
    setObjective('');
    captureEvent('conversation_creation.objective_selected', buildConversationCreationProps(activeWorkspace?.id, { type: 'scratch' }));
    setStep('builder');
  }, [abortGeneration, lastGenAttemptRef, setFields, setGenerationError, setIsGenerating, setAwaitingTitle, activeWorkspace?.id]);

  /**
   * Submit handler used by the chat-input composer. Forwards typed text and
   * any encoded file attachment to the worker. Validation (at least one of
   * the two non-empty) is handled inside the composer.
   */
  const handleChatSubmit = useCallback(
    (text: string, attachment: InlineAttachment | null) => {
      // When the user attaches a file but doesn't type, send a minimal text
      // prompt so the worker still has a directive. Gemini reads the file
      // either way; the prompt anchors task semantics.
      const objective = text.trim() || 'Use the attached file as the brief.';
      captureEvent('conversation_creation.objective_selected', buildConversationCreationProps(activeWorkspace?.id, { type: 'manual', has_attachment: !!attachment }));
      commitObjectiveAndAdvance(objective, 'custom', attachment);
    },
    [commitObjectiveAndAdvance, activeWorkspace?.id],
  );

  // ── Builder ───────────────────────────────────────────────────────────
  const handleFieldsChange = useCallback((next: FormField[]) => {
    if (!fileStorageEnabled) {
      // A file question whose identity is already on the canvas is the
      // author's in-place format change; a new identity was generated.
      const onCanvas = new Set(fieldsRef.current.map((field) => field.id));
      const converted = next.filter((field) => field.type === 'file_upload'
        && onCanvas.has(field.id) && !authoredFileFieldIds.has(field.id));
      if (converted.length > 0) {
        setAuthoredFileFieldIds((previous) => new Set([...previous, ...converted.map((field) => field.id)]));
      }
    }
    setFields(next);
  }, [authoredFileFieldIds, fileStorageEnabled, setFields]);

  /** The builder commits question order and section membership in the same render. */
  const handleDefinitionChange = useCallback((next: FormField[], sections: ConversationSection[]) => {
    handleFieldsChange(next);
    setConversationConfig(previous => sections.length || previous.sections !== undefined ? { ...previous, sections } : previous);
  }, [handleFieldsChange]);

  // ── Edit-mode media provenance ─────────────────────────────────────────
  // Workspace that owns info-block uploads and cleanup. In edit mode this is
  // the CAMPAIGN's workspace (the admin's active workspace can differ from
  // the campaign's); the create flow keeps using the active workspace.
  const builderWorkspaceId = isEditMode
    ? editSession?.brand_id ?? activeWorkspace?.id
    : activeWorkspace?.id;

  // Storage keys of images a SAVED campaign already references. Removing
  // them must never delete the underlying R2 object: the saved config (and
  // in-flight sessions) still render them. In edit mode that is the campaign
  // under edit; in clone mode it is the source campaign, whose keys the clone
  // shares until the author replaces them. Empty in the plain create flow.
  const committedImageKeys = useMemo(() => {
    return new Set(collectQuestionImageStorageKeys([
      ...(editSession?.form_schema ?? []),
      ...(isCloneMode ? initialDefinition.fields : []),
    ], visibleSections)) as ReadonlySet<string>;
  }, [editSession, initialDefinition, isCloneMode, visibleSections]);

  // ── Information-block abandon cleanup ─────────────────────────────────
  // Best-effort deletes uncommitted info-block images if the author leaves
  // the wizard without saving. `getStorageKeys` reads the live `fields` via a
  // ref so it always reflects the current set, not a stale render snapshot.
  // Committed keys are excluded so navigating away from an edit never deletes
  // the saved campaign's media; only images uploaded during this edit are
  // cleaned on abandon.
  const { markSaved } = useInformationBlockAbandonCleanup({
    workspaceId: builderWorkspaceId,
    getStorageKeys: useCallback(
      () =>
        collectQuestionImageStorageKeys(fieldsRef.current)
          .filter((k) => !committedImageKeys.has(k)),
      [committedImageKeys],
    ),
  });

  // ── Publish gate: per-card info-block issues ──────────────────────────
  // A block missing its image or whose caption is still pending blocks publish
  // (the caption is holito's only surrogate for the image at call time).
  const informationBlockIssues = useMemo(
    () => validateInformationBlocks(fields),
    [fields],
  );
  // A calendar block with an unresolved host or an unbookable custom-windows
  // configuration blocks publish the same way an unfinished info block does.
  const calendarIssues = useMemo(
    () => validateCalendarBookings(fields),
    [fields],
  );
  // Blank legacy draft keys are derived before validation. Existing keys retain
  // their exact identity, including when a later explicit key reserves a name.
  const finalizedFields = useMemo(() => {
    const used = new Set(fields.filter((field) => field.name.length > 0).map((field) => field.name));
    return fields.map((field) => {
      if (field.name.length > 0) return field;
      const derived = labelToName(field.label) || labelToName(`question_${field.id}`);
      const base = (/^[a-z]/.test(derived) ? derived : `question_${derived}`).slice(0, FORM_FIELD_LIMITS.identifier);
      let name = base; let suffix = 2;
      while (used.has(name)) { const ending = `_${suffix++}`; name = `${base.slice(0, FORM_FIELD_LIMITS.identifier - ending.length)}${ending}`; }
      used.add(name);
      return { ...field, name };
    });
  }, [fields]);
  const settingsValue = useMemo<ConversationSettingsValues>(() => ({
    max_responses: maxResponses,
    allow_duplicate_emails: allowDuplicateEmails,
    require_name: false,
    require_email: false,
    identity_collection: 'never',
    video_enabled: videoEnabled,
    require_video: requireVideo,
    screen_share_enabled: screenShareEnabled,
    enforce_language: enforceLanguage,
    conversation_config: conversationConfig,
    output_language: outputLanguage,
    completion_action: completionAction,
    completion_message: completionMessage,
    completion_redirect_url: completionRedirectUrl,
    opens_at: opensAt,
    closes_at: closesAt,
  }), [maxResponses, allowDuplicateEmails, videoEnabled, requireVideo, screenShareEnabled,
    enforceLanguage, conversationConfig, outputLanguage, completionAction, completionMessage,
    completionRedirectUrl, opensAt, closesAt]);
  const settingsValidation = validateConversationSettings(settingsValue, { fields: finalizedFields, responseCount, can });

  /** Apply shared settings patches while retaining the builder's draft lifecycle. */
  const handleSettingsChange = (patch: Partial<ConversationSettingsValues>) => {
    if (patch.max_responses !== undefined) setMaxResponses(patch.max_responses);
    if (patch.allow_duplicate_emails !== undefined) setAllowDuplicateEmails(patch.allow_duplicate_emails);
    if (patch.video_enabled !== undefined) setVideoEnabled(patch.video_enabled);
    if (patch.require_video !== undefined) setRequireVideo(patch.require_video);
    if (patch.screen_share_enabled !== undefined) setScreenShareEnabled(patch.screen_share_enabled);
    if (patch.enforce_language !== undefined) setEnforceLanguage(patch.enforce_language);
    if (patch.conversation_config !== undefined) setConversationConfig(patch.conversation_config);
    if (patch.output_language !== undefined) setOutputLanguage(patch.output_language);
    if (patch.completion_action !== undefined) setCompletionAction(patch.completion_action);
    if (patch.completion_message !== undefined) setCompletionMessage(patch.completion_message);
    if (patch.completion_redirect_url !== undefined) setCompletionRedirectUrl(patch.completion_redirect_url);
    if (patch.opens_at !== undefined) setOpensAt(patch.opens_at);
    if (patch.closes_at !== undefined) setClosesAt(patch.closes_at);
  };
  const hasFileQuestions = finalizedFields.some(field => field.type === 'file_upload');
  const storageOrganizationId = editSession?.organization_id ?? activeOrg?.id;
  const missingDestination = hasFileQuestions && !conversationConfig.storageDestination;
  const ordinaryFieldIssues = useMemo(() => validateFormFields(finalizedFields), [finalizedFields]);
  const sectionIssues = useMemo(() => validateConversationSections(finalizedFields, visibleSections), [finalizedFields, visibleSections]);
  const informationBlockIssueByField = useMemo(() => {
    const map = new Map<string, string>();
    for (const issue of informationBlockIssues) {
      map.set(
        issue.fieldId,
        issue.reason === 'missing_image' ? 'Add an image' : 'Caption pending',
      );
    }
    for (const issue of calendarIssues) {
      const hint =
        issue.reason === 'missing_connection'
          ? 'Connect a calendar'
          : issue.reason === 'empty_window'
            ? 'Widen the booking window'
            : 'Enable at least one day';
      map.set(issue.fieldId, hint);
    }
    for (const issue of ordinaryFieldIssues) map.set(issue.fieldId, issue.message);
    return map;
  }, [informationBlockIssues, calendarIssues, ordinaryFieldIssues]);

  // ── Save ──────────────────────────────────────────────────────────────
  const handleCreate = useCallback(async () => {
    if (isGenerating || destinationSaveRef.current || missingDestination || settingsValidation.invalid || validateConversationSections(finalizedFields, visibleSections).length > 0 || validateFormFields(finalizedFields).length > 0) return;
    let validatedConfig = visibleSections === conversationConfig.sections ? conversationConfig : { ...conversationConfig, sections: visibleSections };
    if (hasFileQuestions && conversationConfig.storageDestination) {
      destinationSaveRef.current = true; setCheckingDestination(true); setDestinationError(null);
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession || !storageOrganizationId) throw new Error('Storage session unavailable');
        const { connectionId, folderId } = conversationConfig.storageDestination;
        const response = await fetch('/api/integrations/storage/google/destination', { method: 'POST', headers: { Authorization: `Bearer ${currentSession.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ organizationId: storageOrganizationId, connectionId, folderId, ...(editSession ? { interviewId: editSession.id } : {}) }) });
        // An unwritable folder or lost per-file Drive access is fixed by choosing again, not by retrying.
        if (response.status === 400 || response.status === 404) { setDestinationError('Choose the Drive folder again.'); return; }
        if (!response.ok) throw new Error(`Storage destination unavailable: ${response.status}`);
        const data = await response.json() as { destination: NonNullable<ConversationExperienceConfig['storageDestination']> };
        validatedConfig = { ...validatedConfig, storageDestination: data.destination };
        setConversationConfig(validatedConfig);
      } catch (error) {
        captureException(error as Error, 'CreateInterviewForm.validateStorageDestination', { organizationId: storageOrganizationId });
        setDestinationError('Couldn’t verify the Drive folder. Check access and try saving again.');
        return;
      } finally { destinationSaveRef.current = false; setCheckingDestination(false); }
    }
    // The display title (target_name) is the human-readable name shown in
    // the dashboard, sidebar, and breadcrumbs. We source it from the inline
    // title textarea; if the admin left it blank, fall back to the workspace
    // name so the saved form still has SOME readable label.
    const trimmedTitle = formTitle.trim();
    const context: InterviewContext = {
      targetName: trimmedTitle || targetName,
      targetUrl: targetUrl || undefined,
      goal: objective,
      details: '', // Not authored, service auto-derives from form_schema.
      outputLanguage,
      brandId: activeWorkspace?.id,
    };

    if (productResearch) {
      context.productResearch = {
        summary: productResearch.summary,
        keyFeatures: productResearch.keyFeatures,
        targetAudience: productResearch.targetAudience,
        valueProposition: productResearch.valueProposition,
        brandVoiceTone: productResearch.brandVoiceTone,
        researched_at: new Date().toISOString(),
      };
    }

    // The campaign is being saved: its info-block images are now committed, so
    // opt out of the abandon cleanup before handing off to the parent.
    markSaved();

    const savedSettings = prepareConversationSettingsSave({ ...settingsValue, conversation_config: validatedConfig }, can, interviewLanguagePolicy);
    onSetupComplete(context, {
      conversationConfig: validatedConfig,
      videoEnabled: savedSettings.video_enabled,
      formSchema: finalizedFields,
      campaignSettings: savedSettings,
    });
  }, [isGenerating, visibleSections, conversationConfig, settingsValue, settingsValidation.invalid, can, missingDestination,
    hasFileQuestions, storageOrganizationId, finalizedFields, formTitle, objective,
    onSetupComplete, outputLanguage, productResearch, targetName, targetUrl,
    interviewLanguagePolicy, markSaved, supabase, editSession, activeWorkspace?.id]);

  /**
   * Empty state shown when no workspace is active. Falls back to a simple
   * card with a switch affordance, when the user has more than one
   * workspace we let them pick from the chip; otherwise we tell them to
   * create one from the rail.
   */
  if (!activeWorkspace) {
    return (
      <div
        ref={scrollRef}
        data-testid="no-workspace-empty-state"
        className="flex flex-col items-center justify-center h-full p-8"
      >
        <div className="max-w-md w-full text-center space-y-4 bg-surface/50 border border-border/50 rounded-xl p-8">
          <h2 className="text-xl font-bold text-foreground">No workspace selected</h2>
          <p className="text-muted-foreground text-sm">
            Choose a workspace to get started.
            holito uses your workspace's brand context to tailor every
            objective and question.
          </p>
          {workspaces.length > 1 && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setActiveWorkspaceId(workspaces[0].id)}
                className="rounded-lg px-4 py-2.5 button-accent font-medium transition-colors"
              >
                Use {workspaces[0].name}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Step: Single-row objective list + always-visible free-form input ─
  if (step === 'objectives') {
    // Once the user has typed anything into the composer we collapse the
    // suggestion chip row, they have intent of their own and the starting
    // points are no longer useful clutter. The chips re-appear smoothly if
    // the user clears the textarea.
    const isComposerActive = freeFormObjective.trim().length > 0;

    return (
      <AuthoringGoal ref={scrollRef} title="What should we ask?">
          {/* Chat-style composer is the primary affordance: typing, voice,
              file attach. The workspace dropdown lives inside the composer's
              left-actions slot so the "drafting for" affordance is visually
              part of the chat modal. */}
          <FeatureLesson lesson="create" engaged={isComposerActive}><ObjectiveChatInput
            value={freeFormObjective}
            onChange={setFreeFormObjective}
            onSubmit={handleChatSubmit}
            placeholder="Your goal, questions, or existing link"
            inputLabel="Your goal or questions"
            sendLabel="Build"
            showSendLabel
            leftActions={<WorkspaceDropdown />}
          /></FeatureLesson>

          {/**
           * "Start blank" is rendered as its own row that lives BELOW
           * the AI suggestion list, so it stays reachable in every state
           * (cache hit, empty cache, mid-stagger). The button uses a distinct
           * testid (`objective-start-from-scratch`) so the existing
           * `getAllByTestId('objective-card')` assertions on the AI
           * suggestion count remain stable.
           *
           * There is no error/retry state here: the wizard never calls the
           * LLM directly. Suggestions are populated by
           * {@link components/workspace/WorkspaceSettings} on save, so a
           * null cache simply means "not yet generated for this workspace"
           *, the user opts in to suggestions by saving brand context in
           * settings, or proceeds via Start blank.
           */}
          {
            // Suggestion chips. "Start blank" is rendered FIRST so the
            // escape hatch is always present and never moves; AI suggestions
            // append to its right one at a time on the staggered cadence
            // (`revealedInitialCount` / `revealedExtraCount`). The row is
            // left-aligned (`justify-start`) so each new chip lands to the
            // right of its predecessor without recentering the group, no
            // sibling movement. Per-chip reveal is a pure opacity fade
            // (`animate-fade-content`) so chips don't slide. Hints aren't
            // rendered inline, the chip label IS the affordance, with the
            // hint riding as a native tooltip for users who hover.
            //
            // When the user starts typing into the composer, the whole chip
            // row collapses smoothly (max-height + opacity) so the page
            // declutters as soon as the user has their own intent in mind.
            <div
              data-testid="objective-list"
              aria-hidden={isComposerActive}
              className={`overflow-hidden transition-all duration-300 ease-out ${
                isComposerActive
                  ? 'max-h-0 opacity-0 pointer-events-none'
                  : 'max-h-96 opacity-100'
              }`}
            >
              <div className="flex flex-wrap justify-start gap-2 items-center">
                <button
                  type="button"
                  data-testid="objective-start-from-scratch"
                  onClick={handleStartFromScratch}
                  title={START_FROM_SCRATCH_OBJECTIVE.hint}
                  className="whitespace-nowrap rounded-full border border-border bg-background/60 px-3.5 py-1.5 text-sm text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
                >
                  {START_FROM_SCRATCH_OBJECTIVE.title}
                </button>

                {showResearchPlaceholder && (
                  // Spinner + dynamic line while the worker is still writing
                  // `suggested_objectives`. Live SSE label when present, else
                  // a brand-name fallback.
                  <div
                    data-testid="brand-research-placeholder"
                    className="animate-fade-content inline-flex items-center gap-2 px-1 text-sm text-muted-foreground"
                  >
                    <span
                      data-testid="brand-research-spinner"
                      aria-hidden="true"
                      className="inline-block h-3 w-3 rounded-full border-2 border-border border-t-volt animate-spin"
                    />
                    <span>
                      {researchLabel?.label ??
                        `Tailoring suggestions for ${
                          activeWorkspace?.brand_name_canonical ??
                          activeWorkspace?.name ??
                          'your brand'
                        }`}
                    </span>
                  </div>
                )}

                {!showResearchPlaceholder && suggestedObjectives !== null &&
                  suggestedObjectives
                    .slice(
                      0,
                      showAllObjectives
                        ? Math.min(3, revealedInitialCount) + revealedExtraCount
                        : Math.min(3, revealedInitialCount, suggestedObjectives.length),
                    )
                    .map((row, idx) => (
                      <button
                        key={`${row.templateKey}-${idx}`}
                        type="button"
                        data-testid="objective-card"
                        onClick={() => handleSelectObjective(row)}
                        title={row.hint ?? undefined}
                        className="animate-fade-content whitespace-nowrap rounded-full border border-border bg-background/60 px-3.5 py-1.5 text-sm text-muted-foreground hover:bg-surface-hover hover:text-foreground transition-colors"
                      >
                        {row.title}
                      </button>
                    ))}

                {suggestedObjectives !== null &&
                  !showAllObjectives &&
                  revealedInitialCount >= Math.min(3, suggestedObjectives.length) &&
                  suggestedObjectives.length > 3 && (
                  <button
                    type="button"
                    data-testid="objective-show-more"
                    onClick={handleShowMore}
                    className="animate-fade-content rounded-full px-3.5 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-surface-hover transition-colors"
                  >
                    Show {suggestedObjectives.length - 3} more
                  </button>
                )}
              </div>

              {/* Brand-context tip. With no workspace URL, brand research
                  never runs and the suggestion row stays empty, so we nudge
                  the user toward settings where they can add a URL / brand
                  context and unlock tailored suggestions. Hidden the moment a
                  URL exists (suggestions either render or are mid-research). */}
              {!activeWorkspace?.website_url && (
                <p
                  data-testid="objective-brand-tip"
                  className="mt-3 text-xs text-subtle-foreground"
                >
                  Tip: add brand context in{' '}
                  <a
                    href="/admin/workspace/settings"
                    className="text-volt-soft hover:underline"
                  >
                    workspace settings
                  </a>{' '}
                  for tailored suggestions.
                </p>
              )}
            </div>
          }
      </AuthoringGoal>
    );
  }

  // The host provides the sticky header while the editor retains action state.
  const openSettings = () => {
    captureEvent('conversation_creation.settings_opened', buildConversationCreationProps(activeWorkspace?.id));
    setIsSettingsOpen(true);
  };
  const builderActions = (
    <div className={`flex items-center gap-2 ${actionsTarget !== undefined ? 'justify-end' : 'flex-wrap pt-6 border-t border-border'}`}>
      {!isEditMode && (
        <button
          type="button"
          onClick={handleBackToObjectives}
          className="flex shrink-0 items-center gap-2 whitespace-nowrap px-2 sm:px-4 py-2 text-sm font-bold text-muted-foreground rounded-lg hover:bg-surface-hover hover:text-foreground transition-colors cursor-pointer"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
      )}
      {compactActions ? <DropdownMenu anchor="bottom end" panelZIndex={60}
        trigger={<button type="button" aria-label="More question actions" disabled={savePending}
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-surface-hover hover:text-foreground disabled:opacity-40"><IconDots aria-hidden size={20} /></button>}
        items={[
          { label: 'Settings', onClick: openSettings, disabled: savePending },
          { label: 'Preview', onClick: () => setIsPreviewOpen(true), disabled: savePending || fields.length === 0 },
        ]} /> : <><button
        type="button"
        aria-label="Settings"
        disabled={savePending}
        onClick={openSettings}
        className="ml-auto min-h-11 shrink-0 whitespace-nowrap cursor-pointer rounded-lg px-2 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed motion-reduce:transition-none"
      >
        Settings
      </button>
      <button type="button" onClick={() => setIsPreviewOpen(true)} disabled={savePending || fields.length === 0}
        className="min-h-11 shrink-0 whitespace-nowrap cursor-pointer rounded-lg px-2 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed motion-reduce:transition-none">Preview</button></>}
      <button
        type="button"
        onClick={handleCreate}
        aria-label={isEditMode ? 'Save changes' : 'Create'}
        aria-busy={savePending}
        disabled={
          savePending || isGenerating || checkingDestination || missingDestination ||
          fields.length === 0 ||
          informationBlockIssues.length > 0 ||
          calendarIssues.length > 0 ||
          ordinaryFieldIssues.length > 0 ||
          sectionIssues.length > 0 ||
          settingsValidation.invalid
        }
        className={`flex ${isEditMode && actionsTarget !== undefined ? 'h-9' : 'min-h-11'} ${actionsTarget === undefined ? 'w-full sm:w-auto' : ''} shrink-0 items-center justify-center gap-2 whitespace-nowrap px-4 py-2 ${isEditMode && actionsTarget !== undefined ? 'bg-surface-raised text-foreground hover:bg-surface-hover' : 'button-accent'} font-semibold text-sm rounded-lg transition-colors disabled:bg-surface-raised disabled:text-muted-foreground disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground`}
      >
        <ButtonContent loading={savePending}>{isEditMode
          ? <AnimatedLabel text={saveSucceeded ? 'Saved' : 'Save'} reserveText="Saved" />
          : 'Create'}</ButtonContent>
      </button>
    </div>
  );

  // The shared canvas owns loading and loaded editor geometry.
  return (
    <QuestionEditorSurface scrollRef={scrollRef} busy={savePending} footer={<>
      {actionsTarget !== undefined ? (actionsTarget ? createPortal(builderActions, actionsTarget) : null) : <div className="max-w-3xl w-full mx-auto">{builderActions}</div>}

      {isPreviewOpen && <QuestionPreview fields={finalizedFields} sections={visibleSections} onClose={() => setIsPreviewOpen(false)} />}

      {/* Slide-over: advanced settings (relocated from inline panel) */}
      <ConversationSettingsPanel open={isSettingsOpen} onClose={() => setIsSettingsOpen(false)}
        value={settingsValue} onChange={handleSettingsChange} fields={finalizedFields} responseCount={responseCount} />
    </>}>
        {/* Notion-style page title, the form's objective doubles as its
            headline. Inline editable, no border at rest. A subtle
            background tint appears on hover and focus so the user can
            tell it's an editable surface (otherwise it reads as static
            text). We use a textarea so long objectives wrap onto
            multiple lines instead of being clipped by a single-line
            input.

            While the AI is still generating AND the title slot is
            empty, render a skeleton bar in place of the textarea so the
            slot reads as still-loading instead of showing the
            "Untitled" placeholder. The skeleton's height matches the
            title's line-height so swap-in causes zero layout shift. */}
        {awaitingTitle ? (
          <div
            data-testid="form-builder-title-skeleton"
            aria-busy="true"
            aria-hidden="true"
            className="px-2 py-1 -ml-2 text-3xl font-bold tracking-tight"
          >
            <Skeleton inline width="55%" borderRadius={8} />
          </div>
        ) : (
          <textarea
            ref={titleRef}
            data-testid="form-builder-title"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            onKeyDown={(e) => {
              // Suppress Enter so the title stays a single visual paragraph
              // (Notion's title behaves the same, Enter doesn't add a
              // newline, it submits / advances).
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder="Untitled"
            rows={1}
            className="w-full resize-none overflow-hidden rounded-lg bg-transparent px-2 py-1 -ml-2 text-3xl font-bold tracking-tight text-foreground placeholder:text-subtle-foreground hover:bg-surface-hover/40 focus:bg-surface/40 focus:outline-none transition-colors"
          />
        )}

        {/* Visible failure, NO silent fallback to template starter fields.
            The alert region exists before any failure so screen readers
            announce inserted copy. Copy comes from the public code only;
            retry appears only when another attempt can change the outcome. */}
        <div
          data-testid={generationError ? 'generation-error' : undefined}
          className={generationError ? 'rounded-xl bg-wash-danger p-5 space-y-3' : undefined}
        >
          <p
            role="alert"
            aria-atomic="true"
            data-testid="generation-alert"
            className="text-foreground font-semibold break-words"
          >
            {generationError ? resolveGenerationErrorMessage('form', generationError.code, generationError.retryable) : null}
          </p>
          {generationError && (
            <div className="flex items-center gap-3 pt-1">
              {generationError.retryable && (
                <button
                  ref={generationActionRef}
                  type="button"
                  data-testid="generation-retry"
                  onClick={handleRetryGeneration}
                  className="px-4 py-2 button-accent text-sm font-medium rounded-lg transition-colors"
                >
                  Try again
                </button>
              )}
              <button
                ref={generationError.retryable ? undefined : generationActionRef}
                type="button"
                onClick={handleBackToObjectives}
                className="px-4 py-2 text-muted-foreground text-sm font-medium hover:text-foreground transition-colors"
              >
                Change your brief
              </button>
            </div>
          )}
        </div>
        {generationError === null && (
          <>
          {importReport && <FormImportNotes report={importReport.report} />}
          <FormBuilder
            productResearch={productResearch}
            fileDestination={fileStorageEnabled && storageOrganizationId ? content => <StorageDestinationPicker organizationId={storageOrganizationId} value={conversationConfig.storageDestination} onChange={(storageDestination) => { setConversationConfig(previous => ({ ...previous, storageDestination })); setDestinationError(null); }}>{content}</StorageDestinationPicker> : undefined}
            callsEnabled={conversationConfig.callsEnabled}
            fields={fields}
            onChange={handleFieldsChange}
            sections={visibleSections}
            onDefinitionChange={handleDefinitionChange}
            isGenerating={isGenerating}
            organizationId={storageOrganizationId}
            campaignId={editSession?.id}
            workspaceId={builderWorkspaceId}
            campaignGoal={objective}
            informationBlockIssues={informationBlockIssueByField}
            committedImageKeys={committedImageKeys}
          />
          </>
        )}

        <AnimatedPresence show={sectionIssues.length > 0} className="!my-0"><p role="alert" className="pb-6 text-sm text-danger"><AnimatedLabel text={sectionIssues[0]?.message ?? ''} multiline /></p></AnimatedPresence>
        <AnimatedPresence show={Boolean(destinationError)} className="!my-0"><p role="alert" className="pb-6 text-sm text-danger"><AnimatedLabel text={destinationError ?? ''} multiline /></p></AnimatedPresence>
        <AnimatedPresence show={missingDestination} className="!my-0"><p role="status" className="pb-6 text-sm text-muted-foreground">Choose a Drive folder to save.</p></AnimatedPresence>

    </QuestionEditorSurface>
  );
};

export default CreateInterviewForm;
